import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { Checkbox, FormLabel } from '@mui/material';
import { conventionCurrencyCodeVar } from 'features/insuranceConventions/edit/variables';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { usePermission } from 'providers/RightsHooks';
import { valueOrDefault } from 'utils/functions';
import { emptyString } from 'utils/constants';

const InsuranceConventionCoefficientEditComponent = ({
  coefficientLens,
  validation,
  insuranceAdditionalDataTypes,
  insuranceAdditionalDataTypeDetails,
  onAdditionalDataTypeChanged,
  onAdditionalDataTypeDetailChanged
}) => {
  const currencyCode = useReactiveVar(conventionCurrencyCodeVar);
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);
  const { t } = useTranslation();

  const handlePropertyChange = useCallback(
    property => value => {
      if (value === emptyString) return (coefficientLens[property] |> set)(null);

      return (coefficientLens[property] |> set)(value);
    },
    [coefficientLens]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <Autocomplete
          label={t('InsuranceConventions.Details.Coefficients.Coefficient')}
          required
          valueKey='additionalDataTypeId'
          labelKey='additionalDataTypeName'
          value={{
            additionalDataTypeId: coefficientLens.additionalDataTypeId |> get,
            additionalDataTypeName: coefficientLens.additionalDataTypeName |> get
          }}
          onChange={onAdditionalDataTypeChanged}
          options={insuranceAdditionalDataTypes || []}
          error={!isValid(validation.additionalDataTypeId)}
          helperText={getErrors(validation.additionalDataTypeId)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={5}>
        <Autocomplete
          label={t('InsuranceConventions.Details.Coefficients.Detail')}
          required
          valueKey='detailId'
          labelKey='detailName'
          value={{
            detailId: coefficientLens.detailId |> get,
            detailName: coefficientLens.detailName |> get
          }}
          onChange={onAdditionalDataTypeDetailChanged}
          options={insuranceAdditionalDataTypeDetails || []}
          error={!isValid(validation.detailId)}
          helperText={getErrors(validation.detailId)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={2}>
        <Checkbox
          checked={coefficientLens.isDefault |> get}
          onClick={event => (coefficientLens.isDefault |> set)(event.target.checked)}
          disabled={!canWrite}
        />
        <FormLabel>{t('InsuranceConventions.Details.Coefficients.Default')}</FormLabel>
      </Grid>
      <Grid item xs={5}>
        <TextField
          label={t('InsuranceConventions.Details.Coefficients.RelativePercent')}
          isNumeric
          fullWidth
          value={(coefficientLens.relativePercent |> get) || ''}
          decimalScale={cvsChPercent}
          onChange={handlePropertyChange('relativePercent')}
          isClearable
          error={!isValid(validation.relativePercent)}
          helperText={getErrors(validation.relativePercent)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={7}>
        <TextField
          label={t('InsuranceConventions.Details.Coefficients.PercentFormula')}
          fullWidth
          value={coefficientLens.percentFormula |> get |> valueOrDefault(emptyString)}
          onChange={handlePropertyChange('percentFormula')}
          isClearable
          error={!isValid(validation.percentFormula)}
          helperText={getErrors(validation.percentFormula)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={5}>
        <TextField
          label={t('InsuranceConventions.Details.Coefficients.Amount', { currencyCode: currencyCode })}
          isNumeric
          fullWidth
          value={(coefficientLens.amount |> get) || ''}
          decimalScale={cvsChAmount}
          onChange={handlePropertyChange('amount')}
          isClearable
          error={!isValid(validation.amount)}
          helperText={getErrors(validation.amount)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={7}>
        <TextField
          label={t('InsuranceConventions.Details.Coefficients.AmountFormula')}
          fullWidth
          value={coefficientLens.amountFormula |> get |> valueOrDefault(emptyString)}
          onChange={handlePropertyChange('amountFormula')}
          isClearable
          error={!isValid(validation.amountFormula)}
          helperText={getErrors(validation.amountFormula)}
          disabled={!canWrite}
        />
      </Grid>
    </Grid>
  );
};

InsuranceConventionCoefficientEditComponent.propTypes = {
  coefficientLens: PropTypes.object,
  validation: PropTypes.object,
  insuranceAdditionalDataTypes: PropTypes.array,
  insuranceAdditionalDataTypeDetails: PropTypes.array,
  onAdditionalDataTypeChanged: PropTypes.func,
  onAdditionalDataTypeDetailChanged: PropTypes.func
};

export default InsuranceConventionCoefficientEditComponent;
